import { uploadDocumentToR2 } from './r2Storage';

/**
 * Returns whether a given URL points to Cloudinary storage
 * @param {string} url
 * @returns {boolean}
 */
export const isCloudinaryUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.toLowerCase().includes('cloudinary');
};

/**
 * Returns whether a given URL points to Cloudflare R2 storage
 * @param {string} url
 * @returns {boolean}
 */
export const isR2Url = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return lower.includes('r2.dev') || lower.includes('workers.dev') || lower.includes('r2');
};

/**
 * Identifies the storage provider for a given asset URL
 * @param {string} url
 * @returns {'Cloudflare R2' | 'Cloudinary' | 'Other'}
 */
export const getStorageProvider = (url) => {
  if (!url || typeof url !== 'string') return 'Other';
  if (isR2Url(url)) return 'Cloudflare R2';
  if (isCloudinaryUrl(url)) return 'Cloudinary';
  return 'Other';
};

/**
 * Scans a list of receipts and categorizes their storage providers
 * @param {Array} receipts
 * @returns {{ total: number, r2Count: number, cloudinaryCount: number, otherCount: number, unmigrated: Array }}
 */
export const scanReceiptStorage = (receipts = []) => {
  const safeReceipts = Array.isArray(receipts) ? receipts : [];
  let r2Count = 0;
  let cloudinaryCount = 0;
  let otherCount = 0;
  const unmigrated = [];

  for (const item of safeReceipts) {
    const url = item?.receipt_image_url;
    if (isR2Url(url)) {
      r2Count++;
    } else if (isCloudinaryUrl(url)) {
      cloudinaryCount++;
      unmigrated.push(item);
    } else {
      otherCount++;
    }
  }

  return {
    total: safeReceipts.length,
    r2Count,
    cloudinaryCount,
    otherCount,
    unmigrated,
  };
};

/**
 * Migrates a single receipt image from its current storage (Cloudinary) to Cloudflare R2
 * @param {object} receipt - Receipt database record
 * @param {object} supabase - Supabase client instance
 * @returns {Promise<{ success: boolean, newUrl: string }>}
 */
export const migrateSingleReceipt = async (receipt, supabase) => {
  if (!receipt || !receipt.receipt_image_url) {
    throw new Error('Receipt does not contain an image URL');
  }

  // 1. Fetch the image blob
  const response = await fetch(receipt.receipt_image_url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (${response.status} ${response.statusText})`);
  }
  const blob = await response.blob();

  // 2. Prepare File object
  const mimeType = blob.type || 'image/jpeg';
  const ext = mimeType.split('/')[1] || 'jpg';
  const cleanExt = ext.replace('+xml', '');
  const receiptNum = receipt.receipt_number || receipt.id || 'receipt';
  const safeFilename = `receipt_${String(receiptNum).replace(/[^a-zA-Z0-9_-]/g, '_')}_${Date.now()}.${cleanExt}`;

  let fileObj;
  try {
    fileObj = new File([blob], safeFilename, { type: mimeType });
  } catch (e) {
    blob.name = safeFilename;
    fileObj = blob;
  }

  // 3. Upload to Cloudflare R2
  const newUrl = await uploadDocumentToR2(fileObj, 'receipts');
  if (!newUrl) {
    throw new Error('Upload to R2 returned an empty URL');
  }

  // 4. Update receipts table in Supabase
  if (supabase) {
    const { error: updateError } = await supabase
      .from('receipts')
      .update({
        receipt_image_url: newUrl,
        last_updated: new Date().toISOString(),
      })
      .eq('id', receipt.id);

    if (updateError) {
      throw updateError;
    }

    // 5. Insert audit log into logs table
    try {
      await supabase.from('logs').insert([
        {
          item_name: receipt.receipt_number || 'ใบเสร็จ',
          warehouse: 'Cloudflare R2',
          action: 'ย้ายรูปใบเสร็จจาก Cloudinary ไป Cloudflare R2 สำเร็จ',
        },
      ]);
    } catch (logErr) {
      console.warn('[Migration] Failed to record audit log:', logErr);
    }
  }

  return { success: true, newUrl };
};

/**
 * Batch migrates receipts sequentially with progress reporting
 * @param {Array} receiptList - Array of receipt objects to migrate
 * @param {object} supabase - Supabase client instance
 * @param {Function} onProgress - Callback ({ current, total, item, status, error, newUrl })
 * @returns {Promise<{ successCount: number, failedCount: number, errors: Array }>}
 */
export const batchMigrateReceipts = async (receiptList = [], supabase, onProgress) => {
  let successCount = 0;
  let failedCount = 0;
  const errors = [];
  const safeList = Array.isArray(receiptList) ? receiptList : [];
  const total = safeList.length;

  for (let i = 0; i < total; i++) {
    const item = safeList[i];
    const current = i + 1;

    if (typeof onProgress === 'function') {
      onProgress({
        current,
        total,
        item,
        status: 'migrating',
      });
    }

    try {
      const result = await migrateSingleReceipt(item, supabase);
      successCount++;
      if (typeof onProgress === 'function') {
        onProgress({
          current,
          total,
          item,
          status: 'success',
          newUrl: result.newUrl,
        });
      }
    } catch (err) {
      failedCount++;
      const errorMessage = err?.message || String(err);
      errors.push({ item, error: errorMessage });
      if (typeof onProgress === 'function') {
        onProgress({
          current,
          total,
          item,
          status: 'failed',
          error: errorMessage,
        });
      }
    }
  }

  return { successCount, failedCount, errors };
};

/**
 * Uploads a replacement image directly to R2 and updates the receipt record
 * @param {string} receiptId - UUID of receipt
 * @param {File|Blob} newFile - New file to upload
 * @param {object} supabase - Supabase client instance
 * @returns {Promise<{ success: boolean, newUrl: string }>}
 */
export const replaceReceiptImage = async (receiptId, newFile, supabase) => {
  if (!receiptId) throw new Error('Missing receipt ID');
  if (!newFile) throw new Error('Missing new image file');

  const newUrl = await uploadDocumentToR2(newFile, 'receipts');
  if (!newUrl) throw new Error('Failed to upload replacement file to R2');

  if (supabase) {
    const { error: updateError } = await supabase
      .from('receipts')
      .update({
        receipt_image_url: newUrl,
        last_updated: new Date().toISOString(),
      })
      .eq('id', receiptId);

    if (updateError) throw updateError;

    try {
      await supabase.from('logs').insert([
        {
          item_name: `ใบเสร็จ ID #${receiptId}`,
          warehouse: 'Cloudflare R2',
          action: 'เปลี่ยนรูปภาพใบเสร็จสำเร็จ',
        },
      ]);
    } catch (logErr) {
      console.warn('[Migration] Failed to record replace audit log:', logErr);
    }
  }

  return { success: true, newUrl };
};

/**
 * Helper to extract all image URLs from an item_master image_url field
 * Supporting plain string, JSON string / object { full, zoom, other, document }, or pipe-delimited format
 * @param {string|object} rawImageUrl
 * @returns {Array<string>}
 */
export const getItemImageUrls = (rawImageUrl) => {
  if (!rawImageUrl) return [];
  if (typeof rawImageUrl === 'object') {
    return Object.values(rawImageUrl).filter((v) => typeof v === 'string' && v.trim().length > 0);
  }
  if (typeof rawImageUrl !== 'string') return [];

  const trimmed = rawImageUrl.trim();
  if (!trimmed) return [];

  // Try JSON
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return Object.values(parsed).filter((v) => typeof v === 'string' && v.trim().length > 0);
      }
    } catch (e) {
      // not JSON, fallback to other formats
    }
  }

  // Check pipe-delimited format
  if (trimmed.includes('|')) {
    return trimmed
      .split('|')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  }

  // Plain string URL
  return [trimmed];
};

/**
 * Scans a list of item_master records and categorizes their storage providers
 * @param {Array} items
 * @returns {{ total: number, r2Count: number, cloudinaryCount: number, otherCount: number, unmigrated: Array }}
 */
export const scanItemStorage = (items = []) => {
  const safeItems = Array.isArray(items) ? items : [];
  let r2Count = 0;
  let cloudinaryCount = 0;
  let otherCount = 0;
  const unmigrated = [];

  for (const item of safeItems) {
    const urls = getItemImageUrls(item?.image_url);
    if (urls.length === 0) {
      otherCount++;
      continue;
    }

    const hasCloudinary = urls.some((url) => isCloudinaryUrl(url));
    const allR2 = urls.every((url) => isR2Url(url));

    if (hasCloudinary) {
      cloudinaryCount++;
      unmigrated.push(item);
    } else if (allR2) {
      r2Count++;
    } else {
      otherCount++;
    }
  }

  return {
    total: safeItems.length,
    r2Count,
    cloudinaryCount,
    otherCount,
    unmigrated,
  };
};

/**
 * Migrates a single item_master record from Cloudinary to Cloudflare R2
 * Handles plain string, pipe-delimited, and JSON string/object formats
 * @param {object} item - item_master record
 * @param {object} supabase - Supabase client instance
 * @returns {Promise<{ success: boolean, newUrl: string|object }>}
 */
export const migrateSingleItem = async (item, supabase) => {
  if (!item || !item.image_url) {
    throw new Error('Item does not contain an image URL');
  }

  const raw = item.image_url;
  let updatedImageUrl = raw;
  let hasChanges = false;

  const migrateUrl = async (url, suffix = '') => {
    if (!isCloudinaryUrl(url)) return url;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image (${response.status} ${response.statusText})`);
    }
    const blob = await response.blob();
    const mimeType = blob.type || 'image/jpeg';
    const ext = mimeType.split('/')[1] || 'jpg';
    const cleanExt = ext.replace('+xml', '');
    const itemName = item.item_name || item.id || 'item';
    const safeName = String(itemName).replace(/[^a-zA-Z0-9_-]/g, '_');
    const safeFilename = `item_${safeName}${suffix ? `_${suffix}` : ''}_${Date.now()}.${cleanExt}`;

    let fileObj;
    try {
      fileObj = new File([blob], safeFilename, { type: mimeType });
    } catch (e) {
      blob.name = safeFilename;
      fileObj = blob;
    }

    const newUrl = await uploadDocumentToR2(fileObj, 'items');
    if (!newUrl) {
      throw new Error('Upload to R2 returned an empty URL');
    }
    hasChanges = true;
    return newUrl;
  };

  // 1. JSON format (parsed object or JSON string)
  let isJson = false;
  let parsedObj = null;

  if (typeof raw === 'object' && raw !== null) {
    isJson = true;
    parsedObj = { ...raw };
  } else if (typeof raw === 'string' && raw.trim().startsWith('{') && raw.trim().endsWith('}')) {
    try {
      parsedObj = JSON.parse(raw.trim());
      isJson = true;
    } catch (e) {
      isJson = false;
    }
  }

  if (isJson && parsedObj && typeof parsedObj === 'object') {
    for (const key of Object.keys(parsedObj)) {
      const val = parsedObj[key];
      if (typeof val === 'string' && isCloudinaryUrl(val)) {
        parsedObj[key] = await migrateUrl(val, key);
      }
    }
    updatedImageUrl = JSON.stringify(parsedObj);
  } else if (typeof raw === 'string' && raw.includes('|')) {
    // 2. Pipe-delimited format
    const slots = ['full', 'zoom', 'other', 'document'];
    const parts = raw.split('|');
    for (let i = 0; i < parts.length; i++) {
      const val = parts[i]?.trim();
      if (val && isCloudinaryUrl(val)) {
        parts[i] = await migrateUrl(val, slots[i] || `slot_${i}`);
      }
    }
    updatedImageUrl = parts.join('|');
  } else if (typeof raw === 'string') {
    // 3. Plain string
    if (isCloudinaryUrl(raw)) {
      updatedImageUrl = await migrateUrl(raw);
    }
  }

  if (hasChanges && supabase) {
    const { error: updateError } = await supabase
      .from('item_master')
      .update({
        image_url: updatedImageUrl,
        last_updated: new Date().toISOString(),
      })
      .eq('id', item.id);

    if (updateError) {
      throw updateError;
    }

    try {
      await supabase.from('logs').insert([
        {
          item_name: item.item_name || 'วัสดุ',
          warehouse: 'Cloudflare R2',
          action: 'ย้ายรูปภาพวัสดุจาก Cloudinary ไป Cloudflare R2 สำเร็จ',
        },
      ]);
    } catch (logErr) {
      console.warn('[Migration] Failed to record audit log:', logErr);
    }
  }

  return { success: true, newUrl: updatedImageUrl };
};

/**
 * Batch migrates item_master records sequentially with progress reporting
 * @param {Array} itemList - Array of item records to migrate
 * @param {object} supabase - Supabase client instance
 * @param {Function} onProgress - Callback ({ current, total, item, status, error, newUrl })
 * @returns {Promise<{ successCount: number, failedCount: number, errors: Array }>}
 */
export const batchMigrateItems = async (itemList = [], supabase, onProgress) => {
  let successCount = 0;
  let failedCount = 0;
  const errors = [];
  const safeList = Array.isArray(itemList) ? itemList : [];
  const total = safeList.length;

  for (let i = 0; i < total; i++) {
    const item = safeList[i];
    const current = i + 1;

    if (typeof onProgress === 'function') {
      onProgress({
        current,
        total,
        item,
        status: 'migrating',
      });
    }

    try {
      const result = await migrateSingleItem(item, supabase);
      successCount++;
      if (typeof onProgress === 'function') {
        onProgress({
          current,
          total,
          item,
          status: 'success',
          newUrl: result.newUrl,
        });
      }
    } catch (err) {
      failedCount++;
      const errorMessage = err?.message || String(err);
      errors.push({ item, error: errorMessage });
      if (typeof onProgress === 'function') {
        onProgress({
          current,
          total,
          item,
          status: 'failed',
          error: errorMessage,
        });
      }
    }
  }

  return { successCount, failedCount, errors };
};

/**
 * Scans a list of instrument_tags and categorizes their storage providers
 * @param {Array} tags
 * @returns {{ total: number, r2Count: number, cloudinaryCount: number, otherCount: number, unmigrated: Array }}
 */
export const scanTagStorage = (tags = []) => {
  const safeTags = Array.isArray(tags) ? tags : [];
  let r2Count = 0;
  let cloudinaryCount = 0;
  let otherCount = 0;
  const unmigrated = [];

  for (const tag of safeTags) {
    const url = tag?.image_url;
    if (isR2Url(url)) {
      r2Count++;
    } else if (isCloudinaryUrl(url)) {
      cloudinaryCount++;
      unmigrated.push(tag);
    } else {
      otherCount++;
    }
  }

  return {
    total: safeTags.length,
    r2Count,
    cloudinaryCount,
    otherCount,
    unmigrated,
  };
};

/**
 * Migrates a single instrument tag image from Cloudinary to Cloudflare R2
 * @param {object} tag - instrument_tags database record
 * @param {object} supabase - Supabase client instance
 * @returns {Promise<{ success: boolean, newUrl: string }>}
 */
export const migrateSingleTag = async (tag, supabase) => {
  if (!tag || !tag.image_url) {
    throw new Error('Tag does not contain an image URL');
  }

  // 1. Fetch the image blob
  const response = await fetch(tag.image_url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (${response.status} ${response.statusText})`);
  }
  const blob = await response.blob();

  // 2. Prepare File object
  const mimeType = blob.type || 'image/jpeg';
  const ext = mimeType.split('/')[1] || 'jpg';
  const cleanExt = ext.replace('+xml', '');
  const tagIdentifier = tag.name || tag.id || 'tag';
  const safeFilename = `tag_${String(tagIdentifier).replace(/[^a-zA-Z0-9_-]/g, '_')}_${Date.now()}.${cleanExt}`;

  let fileObj;
  try {
    fileObj = new File([blob], safeFilename, { type: mimeType });
  } catch (e) {
    blob.name = safeFilename;
    fileObj = blob;
  }

  // 3. Upload to Cloudflare R2
  const newUrl = await uploadDocumentToR2(fileObj, 'tags');
  if (!newUrl) {
    throw new Error('Upload to R2 returned an empty URL');
  }

  // 4. Update instrument_tags table in Supabase
  if (supabase) {
    const { error: updateError } = await supabase
      .from('instrument_tags')
      .update({
        image_url: newUrl,
      })
      .eq('id', tag.id);

    if (updateError) {
      throw updateError;
    }

    try {
      await supabase.from('logs').insert([
        {
          item_name: tag.name || 'ป้ายเครื่องมือ',
          warehouse: 'Cloudflare R2',
          action: 'ย้ายรูปป้ายเครื่องมือจาก Cloudinary ไป Cloudflare R2 สำเร็จ',
        },
      ]);
    } catch (logErr) {
      console.warn('[Migration] Failed to record audit log:', logErr);
    }
  }

  return { success: true, newUrl };
};

/**
 * Batch migrates instrument tags sequentially with progress reporting
 * @param {Array} tagList - Array of tag objects to migrate
 * @param {object} supabase - Supabase client instance
 * @param {Function} onProgress - Callback ({ current, total, item, status, error, newUrl })
 * @returns {Promise<{ successCount: number, failedCount: number, errors: Array }>}
 */
export const batchMigrateTags = async (tagList = [], supabase, onProgress) => {
  let successCount = 0;
  let failedCount = 0;
  const errors = [];
  const safeList = Array.isArray(tagList) ? tagList : [];
  const total = safeList.length;

  for (let i = 0; i < total; i++) {
    const item = safeList[i];
    const current = i + 1;

    if (typeof onProgress === 'function') {
      onProgress({
        current,
        total,
        item,
        status: 'migrating',
      });
    }

    try {
      const result = await migrateSingleTag(item, supabase);
      successCount++;
      if (typeof onProgress === 'function') {
        onProgress({
          current,
          total,
          item,
          status: 'success',
          newUrl: result.newUrl,
        });
      }
    } catch (err) {
      failedCount++;
      const errorMessage = err?.message || String(err);
      errors.push({ item, error: errorMessage });
      if (typeof onProgress === 'function') {
        onProgress({
          current,
          total,
          item,
          status: 'failed',
          error: errorMessage,
        });
      }
    }
  }

  return { successCount, failedCount, errors };
};

/**
 * Pings the Cloudflare R2 Worker endpoint to check service health
 * @param {string} workerUrl - Base URL of R2 worker
 * @returns {Promise<{ online: boolean, message: string }>}
 */
export const checkR2WorkerHealth = async (workerUrl) => {
  if (!workerUrl || typeof workerUrl !== 'string' || !workerUrl.trim()) {
    return { online: false, message: 'Worker URL is not configured' };
  }

  const cleanUrl = workerUrl.trim().replace(/\/+$/, '');

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const response = await fetch(cleanUrl, {
      method: 'GET',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (response.ok) {
      let message = 'Worker is online';
      try {
        const json = await response.json();
        if (json && (json.message || json.status || json.service)) {
          message = json.message || json.status || json.service;
        }
      } catch (e) {
        // response might not be json
      }
      return { online: true, message };
    }

    return {
      online: false,
      message: `Worker returned HTTP ${response.status}: ${response.statusText}`,
    };
  } catch (err) {
    const isTimeout = err?.name === 'AbortError';
    return {
      online: false,
      message: isTimeout ? 'Worker request timed out (6s)' : (err?.message || 'Failed to reach worker'),
    };
  }
};

/**
 * Returns configured R2 worker URL from localStorage or environment
 */
export const getR2WorkerUrl = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem('R2_WORKER_URL');
      if (stored && typeof stored === 'string' && stored.trim()) {
        return stored.trim();
      }
    }
  } catch (e) {}

  let metaEnv = {};
  try {
    // eslint-disable-next-line no-new-func
    metaEnv = Function('return import.meta.env')();
  } catch (e) {}

  return (
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_R2_WORKER_URL) ||
    (typeof process !== 'undefined' && process.env && process.env.VITE_R2_WORKER_URL) ||
    metaEnv.VITE_R2_WORKER_URL ||
    ''
  );
};

/**
 * Returns configured R2 worker Auth Key from localStorage or environment
 */
export const getR2AuthKey = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem('R2_AUTH_KEY');
      if (stored && typeof stored === 'string' && stored.trim()) {
        return stored.trim();
      }
    }
  } catch (e) {}

  let metaEnv = {};
  try {
    // eslint-disable-next-line no-new-func
    metaEnv = Function('return import.meta.env')();
  } catch (e) {}

  return (
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_R2_AUTH_KEY) ||
    (typeof process !== 'undefined' && process.env && process.env.VITE_R2_AUTH_KEY) ||
    metaEnv.VITE_R2_AUTH_KEY ||
    ''
  );
};

/**
 * Saves R2 Worker URL and Auth Key configuration to localStorage
 * @param {string} url - Base URL of Cloudflare Worker
 * @param {string} [authKey=''] - Optional authentication key
 */
export const saveR2WorkerConfig = (url, authKey = '') => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (url && typeof url === 'string' && url.trim()) {
        window.localStorage.setItem('R2_WORKER_URL', url.trim());
      } else {
        window.localStorage.removeItem('R2_WORKER_URL');
      }

      if (authKey && typeof authKey === 'string' && authKey.trim()) {
        window.localStorage.setItem('R2_AUTH_KEY', authKey.trim());
      } else {
        window.localStorage.removeItem('R2_AUTH_KEY');
      }
    }
  } catch (e) {
    console.warn('[Storage Config] Failed to save to localStorage:', e);
  }
};

/**
 * Clears custom R2 Worker URL and Auth Key from localStorage
 */
export const clearR2WorkerConfig = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('R2_WORKER_URL');
      window.localStorage.removeItem('R2_AUTH_KEY');
    }
  } catch (e) {
    console.warn('[Storage Config] Failed to clear localStorage:', e);
  }
};

/**
 * Fetches real-time cloud infrastructure health and quota stats
 * for Cloudflare R2, Cloudinary, and Supabase.
 *
 * @param {string} [workerUrl] - Base URL of R2 Worker Gateway
 * @param {object} [supabase] - Supabase client instance
 * @param {Array} [optionalReceipts=[]] - Optional receipts array for fallback calculation
 * @returns {Promise<{ r2: object, cloudinary: object, supabase: object, timestamp: string }>}
 */
export const fetchCloudInfrastructureStats = async (workerUrl, supabase, optionalReceipts = []) => {
  const getNow = () => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  };

  const targetUrl = typeof workerUrl === 'string' ? workerUrl : getR2WorkerUrl();
  const cleanWorkerUrl = (targetUrl || '').trim().replace(/\/+$/, '');
  const authKey = getR2AuthKey();

  let workerCloudinary = null;
  let r2Stats = null;
  let r2Online = false;
  let r2Latency = null;
  let r2Message = '';

  // 1. Ping Cloudflare R2 Worker Gateway & Fetch Quota
  if (cleanWorkerUrl) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      const headers = {};
      if (authKey) headers['X-Custom-Auth-Key'] = authKey;

      const t0 = getNow();
      const res = await fetch(`${cleanWorkerUrl}/stats`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      r2Latency = Math.round(getNow() - t0);

      if (res.ok) {
        r2Online = true;
        const json = await res.json();
        r2Stats = json?.r2 || null;
        workerCloudinary = json?.cloudinary || null;
        r2Message = 'เชื่อมต่อ Worker Gateway สำเร็จ';
      } else {
        r2Message = `Worker HTTP ${res.status}: ${res.statusText}`;
        try {
          const pingRes = await fetch(cleanWorkerUrl, { method: 'GET' });
          if (pingRes.ok) {
            r2Online = true;
            r2Message = 'Worker ออนไลน์ (ยังไม่เปิดใช้งาน /stats)';
          }
        } catch (e) {
          // ignore ping fallback error
        }
      }
    } catch (err) {
      const isTimeout = err?.name === 'AbortError';
      r2Online = false;
      r2Message = isTimeout ? 'การเชื่อมต่อ Worker หมดเวลา (6s)' : (err?.message || 'ไม่สามารถติดต่อ Worker ได้');
    }
  } else {
    r2Online = false;
    r2Message = 'ไม่ได้กำหนด URL ของ Worker ในระบบ';
  }

  const r2Result = {
    online: r2Online,
    latencyMs: r2Latency,
    message: r2Message,
    configured: Boolean(r2Stats?.configured),
    totalObjects: r2Stats?.totalObjects || 0,
    totalBytes: r2Stats?.totalBytes || 0,
    totalFormatted: r2Stats?.totalFormatted || '0 B',
    maxBytes: r2Stats?.maxBytes || 10 * 1024 * 1024 * 1024,
    maxFormatted: r2Stats?.maxFormatted || '10 GB',
    percentUsed: typeof r2Stats?.percentUsed === 'number' ? r2Stats.percentUsed : 0,
    folders: r2Stats?.folders || {
      receipts: { count: 0, bytes: 0, formatted: '0 B' },
      orders: { count: 0, bytes: 0, formatted: '0 B' },
      risk_reports: { count: 0, bytes: 0, formatted: '0 B' },
      items: { count: 0, bytes: 0, formatted: '0 B' },
      tags: { count: 0, bytes: 0, formatted: '0 B' },
      recalls: { count: 0, bytes: 0, formatted: '0 B' },
      others: { count: 0, bytes: 0, formatted: '0 B' },
    },
  };

  // 2. Ping Supabase Database & Health
  let supaOnline = false;
  let supaLatency = null;
  let supaMessage = '';

  if (supabase) {
    try {
      const t0 = getNow();
      const { error } = await supabase.from('receipts').select('id').limit(1);
      supaLatency = Math.round(getNow() - t0);
      if (error) throw error;
      supaOnline = true;
      supaMessage = 'การเชื่อมต่อฐานข้อมูลปกติ';
    } catch (err) {
      supaOnline = false;
      supaMessage = err.message || 'ไม่สามารถเชื่อมต่อฐานข้อมูลได้';
    }
  } else {
    supaMessage = 'ไม่ได้กำหนด Supabase client';
  }

  const supabaseResult = {
    online: supaOnline,
    latencyMs: supaLatency,
    message: supaMessage,
  };

  // 3. Ping Cloudinary CDN & Compute Quota
  let cdnOnline = false;
  let cdnLatency = null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    const t0 = getNow();
    await fetch('https://res.cloudinary.com', {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    cdnLatency = Math.round(getNow() - t0);
    cdnOnline = true;
  } catch (err) {
    try {
      const t0 = getNow();
      await fetch('https://res.cloudinary.com', { mode: 'no-cors' });
      cdnLatency = Math.round(getNow() - t0);
      cdnOnline = true;
    } catch (e) {
      // In tests or offline environments
      cdnOnline = false;
    }
  }

  let cloudinaryResult = null;

  if (workerCloudinary && workerCloudinary.configured && !workerCloudinary.error) {
    cloudinaryResult = {
      online: cdnOnline || true,
      latencyMs: cdnLatency,
      configured: true,
      liveFromWorker: true,
      cloudName: workerCloudinary.cloudName || 'dwb0oda9m',
      plan: workerCloudinary.plan || 'Free',
      credits: workerCloudinary.credits || { usage: 0, limit: 25, percent_usage: 0 },
      storage: workerCloudinary.storage || { usage_bytes: 0, formatted: '0 B' },
      bandwidth: workerCloudinary.bandwidth || { usage_bytes: 0, formatted: '0 B' },
      last_updated: workerCloudinary.last_updated,
      message: 'ดึงข้อมูลสดผ่าน Worker Secret',
    };
  } else {
    // Fallback: Compute estimate from database records
    let fallbackReceipts = optionalReceipts;
    if (!Array.isArray(fallbackReceipts) || fallbackReceipts.length === 0) {
      if (supabase) {
        try {
          const { data } = await supabase.from('receipts').select('id, receipt_image_url');
          fallbackReceipts = data || [];
        } catch (e) {
          fallbackReceipts = [];
        }
      } else {
        fallbackReceipts = [];
      }
    }

    const scan = scanReceiptStorage(fallbackReceipts);
    const count = scan.cloudinaryCount;
    // Estimate ~350 KB per receipt
    const estimatedMB = Number((count * 0.35).toFixed(2));
    const estimatedCredits = Number((estimatedMB / 1024).toFixed(3));
    const estimatedPercent = Number(((estimatedCredits / 25) * 100).toFixed(2));

    cloudinaryResult = {
      online: cdnOnline,
      latencyMs: cdnLatency,
      configured: false,
      liveFromWorker: false,
      estimated: true,
      cloudName: 'dwb0oda9m',
      plan: 'Free (25 Credits)',
      credits: {
        usage: estimatedCredits,
        limit: 25,
        percent_usage: estimatedPercent,
      },
      storage: {
        usage_bytes: Math.round(estimatedMB * 1024 * 1024),
        formatted: `${estimatedMB} MB (ประมาณการ)`,
      },
      bandwidth: {
        usage_bytes: 0,
        formatted: '0 B',
      },
      receiptsCount: count,
      message: workerCloudinary?.message || 'ยังไม่ได้ตั้งค่า Secret ใน Cloudflare Worker',
      error: workerCloudinary?.error,
    };
  }

  return {
    r2: r2Result,
    cloudinary: cloudinaryResult,
    supabase: supabaseResult,
    timestamp: new Date().toISOString(),
  };
};
